import React, { useState, useRef, useEffect } from "react";
import { FaChevronDown } from "react-icons/fa";

interface CustomSelectProps {
  options: { label: string; value: string }[] | string[];
  value: string;
  onChange: (e: { target: { name: string; value: string } }) => void;
  name: string;
  placeholder?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({ options, value, onChange, name, placeholder = "Select...", icon, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOptionLabel: string = (() => {
    if (!options || options.length === 0) return placeholder;
    if (typeof options[0] === "string") {
      return (options as string[]).find((o) => o === value) || placeholder;
    }
    return (options as { label: string; value: string }[]).find((opt) => opt.value === value)?.label || placeholder;
  })();

  return (
    <div className={`relative w-full ${disabled ? "opacity-75 pointer-events-none" : ""}`} ref={dropdownRef}>
      <div 
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full ${icon ? 'pl-10 pr-4' : 'px-4'} py-2.5 ${disabled ? "bg-alabaster-grey-50" : "bg-white"} border border-platinum-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gunmetal-500/20 focus:border-gunmetal-500 transition-all ${disabled ? "cursor-not-allowed" : "cursor-pointer"} flex justify-between items-center text-sm`}
      >
        {icon && <span className="absolute left-3 text-slate-grey-400">{icon}</span>}
        <span className={`truncate ${value ? "text-gunmetal-700" : "text-slate-grey-400"}`}>
          {selectedOptionLabel === placeholder ? placeholder : selectedOptionLabel}
        </span>
        <FaChevronDown className={`text-slate-grey-400 transition-transform duration-200 flex-shrink-0 ml-2 ${isOpen ? "rotate-180" : ""}`} size={10} />
      </div>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-platinum-200 rounded-lg shadow-xl max-h-60 overflow-y-auto custom-scroll py-1">
          {options.map((opt, i) => {
            const optValue = typeof opt === "string" ? opt : (opt as {value: string}).value;
            const optLabel = typeof opt === "string" ? opt : (opt as {label: string}).label;
            const isSelected = value === optValue;
            
            return (
              <div 
                key={i}
                onClick={() => {
                  onChange({ target: { name, value: optValue } });
                  setIsOpen(false);
                }}
                className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${isSelected ? "bg-gunmetal-50 text-gunmetal-900 font-bold" : "text-slate-grey-600 hover:bg-platinum-50 hover:text-gunmetal-900"}`}
              >
                {optLabel || placeholder}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
